<template>
  <LoadingSpinner v-if="billLoading" />

  <div v-else class="show-bill no-print">
    <AppPageHeader :title="`BILL# ${bill.billNumber}`">
      <template #actions>
        <div class="flex gap-x-2">
          <BaseButton v-if="bill.status !== 'paid'" text="Record Payment" @click="showBillPaymentForm = true" />
          <BaseDropdown
            title="Manage"
            right
            :items="[
              { title: 'Edit Bill', to: { name: 'EditBill', params: { id: bill.id } } },
              { title: 'Print', action: print },
              { title: 'Delete', action: () => showConfirmationModal = true }
              ]"
          />
        </div>
      </template>
    </AppPageHeader>

    <!-- Bill details -->
    <div class="grid grid-cols-6 gap-x-2 border-b border-slate-300 pb-4 mb-4">
      <div class="border-r border-slate-300">
        <h3 class="text-sm font-medium leading-6 text-slate-500 mb-2">Status</h3>
        <BaseBadge
          :variant="bill.status === BILL_STATUS.PAID ? 'success' : 'warning'"
        >
          <span class="capitalize">{{ bill.status }}</span>
        </BaseBadge>
      </div>

      <div class="border-r border-slate-300">
        <h3 class="text-sm font-medium leading-6 text-slate-500 mb-2">Due Date</h3>
        <span class="text-sm font-weight-bold">{{ formatDate(bill.dueDate) }}</span>
      </div>

      <div class="border-r border-slate-300">
        <h3 class="text-sm font-medium leading-6 text-slate-500 mb-2">Bill Date</h3>
        <span class="text-sm font-weight-bold">{{formatDate(bill.billDate) }}</span>
      </div>

      <div class="border-r border-slate-300">
        <h3 class="text-sm font-medium leading-6 text-slate-500 mb-2">{{bill.purchaseOrderId ? 'PO #' : 'WO #'}}</h3>
        <span class="text-sm font-weight-bold">
          <router-link v-if="bill.purchaseOrderId && !POLoading" :to="{ name: 'ShowPurchaseOrder', params: { id: purchaseOrder?.id } }">
            {{ bill.purchaseOrderId }}
          </router-link>
          <router-link v-else-if="bill.workOrderId && !WOLoading" :to="{ name: 'ShowWorkOrder', params: { id: workOrder?.id } }">
            {{ workOrder.workOrderId }}
          </router-link>
        </span>
      </div>

      <div class="border-r border-slate-300">
        <h3 class="text-sm font-medium leading-6 text-slate-500 mb-2">Project</h3>
        <span class="text-sm font-weight-bold">
          <router-link
            v-if="bill.projectId && project"
            :to="{
              name: 'ShowProject',
              params: { customerId: project?.customerId, projectId: project?.id }
            }"
          >
            {{ project?.name }}
          </router-link>
        </span>
      </div>

      <div>
        <h3 class="text-sm font-medium leading-6 text-slate-500 mb-2">
          <span v-if="bill.purchaseOrderId">Vendor</span>
          <span v-else>Subcontractor</span>
        </h3>
        <span class="text-sm font-weight-bold">
          <router-link
            v-if="bill.payee && vendor"
            :to="{ name: 'ShowVendor', params: { id: vendor?.id } }"
          >
            {{ vendor?.name }}
          </router-link>
          <router-link
            v-else-if="bill.payee && sub"
            :to="{ name: 'ShowSubcontractor', params: { id: sub?.id } }"
          >
            {{ sub?.name }}
          </router-link>
        </span>
      </div>
    </div>

    <BaseTable
      :items="bill.items"
      :fields="billItemFields"
      class="mt-5"
    >
      <template #cost-data="{ row }">
        {{ currency(getCost(row)) }}
      </template>
      <template #total-data="{ row }">
        {{ currency(row.quantity * getCost(row)) }}
      </template>
    </BaseTable>

    <hr class="border-b-slate-200 border-2" />

    <!-- totals -->
    <div class="mt-5 flex flex-col bg-slate-100 w-1/3 p-4 rounded-lg sm:ml-auto">
      <div class="flex justify-between mb-4">
        <h3 class="text-sm font-medium leading-6 text-slate-500">Total</h3>
        <span class="text-sm font-weight-bold">
          <span v-if="bill.total">{{ currency(bill.total) }}</span>
          <span v-else>$0.00</span>
        </span>
      </div>

      <div class="flex justify-between">
        <h3 class="text-sm font-medium leading-6 text-slate-500">Balance</h3>
        <span class="text-sm font-weight-bold">
          <span v-if="bill.balance">{{ currency(bill.balance) }}</span>
          <span v-else>$0.00</span>
        </span>
      </div>
    </div>

    <!-- Payment activities -->
     <div class="mt-8">
      <h4>Payments Activities</h4>
      <BaseTable
        v-if="billPayments && billPayments.length > 0"
        :items="billPayments"
        :fields="billPaymentFields"
      >
        <template #date-data="{ row }">
          {{ formatDate(row.date) }}
        </template>

        <template #memo-data="{ row }">
          <span v-html="row.memo" />
        </template>

        <template #amount-data="{ row }">
          {{ currency(row.amount) }}
        </template>

        <template #action-data="{ row }">
          <BaseButton
            :disabled="isDeletingPayment"
            :class="{ 'cursor-not-allowed opacity-50': isDeletingPayment }"
            @click="deletePayment(row.id)"
          >
            <div class="flex justify-between items-center gap-2">
              <span>Delete</span>
              <span
                v-if="isDeletingPayment && deletingPaymentId === row.id"
                class="i-heroicons-arrow-path animate-spin"
              />
            </div>
          </BaseButton>
        </template>
      </BaseTable>
     </div>

     <AppModal
        v-model="showBillPaymentForm"
        title="Record Payment"
        width="w-full md:w-1/2"
        :no-escape="true"
        :no-backdrop="true"
        :no-close-button="true"
      >
        <template #body>
          <BillPaymentForm
            v-model="billPayment"
            :is-pending="isMakingPayment"
            :bill="bill"
            @save="makePayment"
            @cancel="showBillPaymentForm = false"
          />
        </template>
      </AppModal>

    <!-- Documents -->
    <div v-if="bill.attachments.length > 0" class="bg-white rounded-lg shadow p-4">
      <div class="purchase-order">
        <div class="flex flex-wrap">
          <h4 class="pl-3">
            Documents
          </h4>
          <div
            v-for="file in bill.attachments"
            :key="file"
            class="mr-3 md:w-1/4"
          >
            <img
              class="thumb mr-3 w-[90px] h-[90px] object-cover"
              :src="file"
              alt="Document thumbnail"
            />
          </div>
        </div>
      </div>
    </div>

    <DeleteConfirmationModal
      id="deleteConfirmationModal"
      v-model="showConfirmationModal"
      title="Delete Bill"
      message="Are you sure you want to delete this bill?"
      :is-deleting="isDeleting"
      @delete="deleteTheBill"
    />
  </div>

  <div
    v-if="bill && bill.id && (vendor || sub)"
    id="printContent"
    class="hide-print-content"
  >
    <PrintBill
      :bill="bill"
      :name="vendor?.name || sub?.name"
      :address="vendor?.address || sub?.address"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import AppPageHeader from '@/components/app/AppPageHeader.vue'
import BaseDropdown from '@/components/base/BaseDropdown.vue'
import BaseBadge from '@/components/base/BaseBadge.vue'
import BaseTable from '@/components/base/BaseTable.vue'
import AppModal from '@/components/app/AppModal.vue'
import DeleteConfirmationModal from '@/components/app/DeleteConfirmationModal.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import { BILL_STATUS, BillPayment } from '@/models/bill'
import { useRoute, useRouter } from 'vue-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import BillService from '@/services/BillService'
import ProjectService from '@/services/ProjectService'
import SubcontractorService from '@/services/SubcontractorService'
import VendorService from '@/services/VendorService'
import PurchaseOrderService from '@/services/PurchaseOrderService'
import WorkOrderService from '@/services/WorkOrderService'
import { currency, formatDate, toNumber  } from '@/utils'
import PrintBill from '../components/PrintBill.vue'
import { useNotification } from '@kyvg/vue3-notification'
import BillPaymentForm from '../components/BillPaymentForm.vue'
import { PURCHASE_ORDER_STATUS } from '@/models/purchaseOrder'
import BaseButton from '@/components/base/BaseButton.vue'
import { WORK_ORDER_STATUS } from '@/models/workOrder'
import { extend } from 'underscore'

const billId = useRoute().params.id as string
const billItemFields = [
  { key: 'name', label: 'Items' },
  { key: 'cost', label: 'Price' },
  { key: 'quantity', label: 'Quantity' },
  { key: 'total', label: 'Total' }
]
const billPaymentFields = [
  { key: 'date', label: 'Date' },
  { key: 'method', label: 'Method' },
  { key: 'memo', label: 'Memo' },
  { key: 'amount', label: 'Amount' },
  { key: 'action', label: '' }
]
const queryClient = useQueryClient()
const { notify } = useNotification()
const $router = useRouter()
const showConfirmationModal = ref(false)
const showBillPaymentForm = ref(false)
const billPayment = ref(new BillPayment())
const print = () => window.print()

const { data: bill, isLoading: billLoading } = useQuery({
  queryKey: ['bill', billId],
  queryFn: async () =>  new BillService().find(billId),
})

const enableBillPaymentsQuery = computed(() => !!bill.value?.id)
const { data: billPayments } = useQuery({
  queryKey: ['billPayments', billId],
  queryFn: async () => new BillService().getBillPayments(billId),
  enabled: enableBillPaymentsQuery
})

const enableProjectQuery = computed(() => bill.value?.projectId !== '')
const { data: project } = useQuery({
  queryKey: ['BillPR', bill.value?.projectId],
  queryFn: async () => {
    return await new ProjectService().find(bill.value?.projectId)
  },
  enabled:  enableProjectQuery
})

const enableWOQuery = computed(() => !billLoading.value && !!bill.value?.workOrderId)
const { data: workOrder, isLoading:WOLoading } = useQuery({
  queryKey: ['BillWO', bill.value?.workOrderId],
  queryFn: async () => {
    const result = await new WorkOrderService('').find_by('workOrderId', '==', bill.value?.workOrderId)
    return result[0]
  },
  enabled: enableWOQuery
})

const { data: sub } = useQuery({
  queryKey: ['subcontractor', bill.value?.payee],
  queryFn: async () => {
    return await new SubcontractorService().find(bill.value?.payee)
  },
  enabled: () => !!bill.value?.payee && !!bill.value?.workOrderId
})

const { data: vendor } = useQuery({
  queryKey: ['vendor', bill.value?.payee],
  queryFn: async () => {
    return await new VendorService().find(bill.value?.payee)
  },
  enabled: () => !!bill.value?.payee && !!bill.value?.purchaseOrderId
})

const enablePOQuery = computed(() => !billLoading.value && !!bill.value?.purchaseOrderId)
const { data: purchaseOrder, isLoading:POLoading } = useQuery({
  queryKey: ['BillPO', bill.value?.purchaseOrderId],
  queryFn: async () => {
    const result = await new PurchaseOrderService('').find_by('purchaseOrderId', '==', bill.value?.purchaseOrderId)
    return result[0]
  },
  enabled: enablePOQuery
})

const deletingPaymentId = ref('')
const { mutateAsync: deletePayment, isPending: isDeletingPayment } = useMutation({
  mutationFn: async (idPayment: string) => {
    deletingPaymentId.value = idPayment
    const billService = new BillService()
    await billService.deleteBillPayment(bill.value.id, idPayment)

    // Update bill status and balance
    await billService.update(bill.value.id, {
      ...extend({}, bill.value),
      balance: toNumber(bill.value.balance) + toNumber(billPayments.value?.find(p => p.id === idPayment)?.amount || 0),
      status: BILL_STATUS.OUTSTANDING
    })
    // Update related order status
    await updateOrderStatus('open')

    queryClient.invalidateQueries({ queryKey: ['billPayments', billId] })
    queryClient.invalidateQueries({ queryKey: ['bill', billId] })
    queryClient.invalidateQueries({ queryKey: ['firestore', 'bills'], exact: false })
    deletingPaymentId.value = ''

    notify({ type: 'success', text: 'Payment deleted successfully' })
  },
  onError: (error) => {
    console.error(error)
    notify({ type: 'danger', text: 'Failed to delete payment' })
  }
})

const { mutate: updatePurchaseOrderStatus } = useMutation({
  mutationFn: async (data: { id: string, status: string }) => {
    if (!purchaseOrder.value) return
    await new PurchaseOrderService(`budgets/${purchaseOrder.value.projectId}`).update(purchaseOrder.value.id, { ...extend({}, purchaseOrder.value), status: data.status })
    queryClient.invalidateQueries({ queryKey: ['purchaseOrder', purchaseOrder.value.id] })
  },
  onError: (error) => {
    console.error(error)
    notify({ type: 'danger', text: 'Failed to update purchase order' })
  }
})

const { mutate: updateWorkOrderStatus } = useMutation({
  mutationFn: async (data: { id: string, status: string }) => {
    if (!workOrder.value) return
    await new WorkOrderService(`budgets/${workOrder.value.projectId}`).update(workOrder.value.id, { ...extend({}, workOrder.value), status: data.status })
    queryClient.invalidateQueries({ queryKey: ['workOrder', workOrder.value.id] })
  },
  onError: (error) => {
    console.error(error)
    notify({ type: 'danger', text: 'Failed to update work order' })
  }
})

const { mutateAsync: deleteTheBill, isPending: isDeleting } = useMutation({
  mutationFn: async () => {
    await new BillService().deleteBill(bill.value.id)
    await updateOrderStatus('open')
  },
  onSuccess: async () => {
    queryClient.removeQueries({ queryKey: ['bill', billId] })
    await queryClient.invalidateQueries({ queryKey: ['firestore', 'bills'], exact: false })
    queryClient.invalidateQueries({ queryKey: ['existingBills'], exact: false })

    notify({
      type: 'success',
      title: 'Bill Deleted',
      text: 'Bill deleted successfully'
    })

    $router.push({ name: 'ListBills' })
  },
  onError: (error) => {
    showConfirmationModal.value = false
    notify({
      type: 'error',
      title: 'Error deleting bill',
      text: error.message || 'An error occurred while deleting the bill'
    })
  }
})

const getCost = (row: any) => {
  if (row.cost) {
    return row.cost
  } else if (row.unitPrice) {
    return row.unitPrice
  } else {
    return 0
  }
}

const updateOrderStatus = async (status: string) => {
  if (bill.value?.purchaseOrderId) {
    updatePurchaseOrderStatus({
      id: bill.value.purchaseOrderId,
      status: status === 'close' ? PURCHASE_ORDER_STATUS.CLOSED : PURCHASE_ORDER_STATUS.OPEN
    })
  } else if (bill.value?.workOrderId) {
    updateWorkOrderStatus({
      id: bill.value.workOrderId,
      status: status === 'close' ? WORK_ORDER_STATUS.CLOSED : WORK_ORDER_STATUS.OPEN
    })
  }
}

const { mutateAsync: makePayment, isPending: isMakingPayment } = useMutation({
  mutationFn: async (payment: BillPayment) => {
    try {
      const billService = new BillService()

    // Save the payment
    await billService.addBillPayment(bill.value.id, payment)

    // Calculate new balance
    const newBalance = toNumber(bill.value.balance) - toNumber(payment.amount)

    // Update bill status and balance
    const updatedBill = {
      ...bill.value,
      balance: newBalance,
      status: newBalance <= 0 ? BILL_STATUS.PAID : bill.value.status
    }

    await billService.update(bill.value.id, updatedBill)

    // If bill is paid, update related order status
    if (newBalance <= 0) {
      await updateOrderStatus('close')
    }

    // Refresh the bill data
    queryClient.invalidateQueries({ queryKey: ['bill', billId] })
    queryClient.invalidateQueries({ queryKey: ['billPayments', billId] })
    queryClient.invalidateQueries({ queryKey: ['firestore', 'bills'], exact: false })

    // Close the payment form
    showBillPaymentForm.value = false

    // Reset payment form
    billPayment.value = new BillPayment()

    // Show success notification
    notify({
      type: 'success',
      title: 'Payment Recorded',
      text: 'Payment has been successfully recorded'
    })
  } catch (error) {
    console.error(error)
    notify({
      type: 'error',
      title: 'Error Recording Payment',
      text: error.message || 'An error occurred while recording the payment'
    })
  }
}})
</script>


<style lang="scss" scoped>
@media print {
  .no-print {
    display: none;
  }
  #printContent {
    display: block;
  }
}

.hide-print-content {
  display: none;
}
</style>
